import React from 'react';
import { Plus, Trash2 } from 'lucide-react';

const HTTP_URL_PATTERN = /^https?:\/\/[^\s/$.?#].[^\s]*$/i;
const MAX_LINKS = 20;
const MAX_URL_LENGTH = 2048;

export function isValidHttpUrl(value: string): boolean {
  const text = value.trim();
  if (!text) return true;
  if (text.length > MAX_URL_LENGTH) return false;
  try {
    const u = new URL(text);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return false;
    if (!u.hostname) return false;
    return HTTP_URL_PATTERN.test(text);
  } catch {
    return false;
  }
}

/** Returns cleaned unique URLs, or null when empty. Throws Error with message on invalid. */
export function validateAndNormalizeReferenceLinks(links: string[]): string[] | null {
  const cleaned = links.map((l) => l.trim()).filter(Boolean);
  if (cleaned.length > MAX_LINKS) {
    throw new Error(`You can add at most ${MAX_LINKS} reference links`);
  }
  const seen = new Set<string>();
  const out: string[] = [];
  for (const link of cleaned) {
    if (!isValidHttpUrl(link)) {
      throw new Error(`Invalid URL: "${link}". Use a full http:// or https:// link.`);
    }
    if (link.length > MAX_URL_LENGTH) {
      throw new Error('One of the reference URLs is too long');
    }
    const key = link.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(link);
  }
  return out.length ? out : null;
}

type ReferenceLinksFieldProps = {
  value: string[];
  onChange: (next: string[]) => void;
};

/**
 * Multi-URL reference links editor for writer / doctor blog forms.
 */
const ReferenceLinksField: React.FC<ReferenceLinksFieldProps> = ({ value, onChange }) => {
  const rows = value.length ? value : [''];

  const setAt = (index: number, text: string) => {
    const next = [...rows];
    next[index] = text;
    onChange(next);
  };

  const addRow = () => {
    if (rows.length >= MAX_LINKS) return;
    onChange([...rows, '']);
  };

  const removeRow = (index: number) => {
    if (rows.length <= 1) {
      onChange(['']);
      return;
    }
    onChange(rows.filter((_, i) => i !== index));
  };

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Reference links (optional)
        </label>
        <button
          type="button"
          onClick={addRow}
          disabled={rows.length >= MAX_LINKS}
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50 disabled:opacity-50 dark:text-primary-400 dark:hover:bg-primary-900/20"
        >
          <Plus className="h-3.5 w-3.5" />
          Add URL
        </button>
      </div>
      <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
        Add one or more http(s) URLs shown on the blog detail page. Leave empty to hide the section.
      </p>
      <div className="space-y-2">
        {rows.map((link, index) => {
          const trimmed = link.trim();
          const invalid = trimmed.length > 0 && !isValidHttpUrl(trimmed);
          return (
            <div key={index} className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <input
                  type="url"
                  value={link}
                  onChange={(e) => setAt(index, e.target.value)}
                  placeholder="https://example.com/article"
                  className={`w-full rounded-lg border px-4 py-2.5 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 ${
                    invalid
                      ? 'border-red-400 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 dark:border-gray-700'
                  }`}
                />
                {invalid ? (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                    Enter a valid http:// or https:// URL
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => removeRow(index)}
                className="mt-2 rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-red-600 dark:hover:bg-gray-700"
                title="Remove"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ReferenceLinksField;
